import axios from 'axios';
import { supabase } from './supabaseClient';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export const api = axios.create({
  baseURL: API,
});

// Calling supabase.auth.getSession() from the request interceptor (the
// previous approach) meant every request re-entered GoTrueClient's internal
// session lock independently. Harmless one at a time, but a page that fires
// several requests in parallel on first load (e.g. a project detail deep
// link — 8 concurrent api.get() calls) could pile up that many concurrent
// getSession() calls right as the very first one (from AuthProvider's own
// bootstrap) was still resolving the persisted session, and that lock
// contention could hang indefinitely — a known supabase-js footgun, not
// anything server-side (confirmed: the same requests succeed instantly
// when made directly with a token, and plain in-app navigation, which
// never fires 8 requests at once on a cold client, never hit it).
// Tracking the token here instead, kept current via onAuthStateChange
// (which fires immediately with whatever session is already persisted —
// supabase-js's INITIAL_SESSION event — then again on every sign-in/
// refresh/sign-out), means the interceptor only ever reads a plain
// variable: no lock, no await, no race, regardless of how many requests
// fire at once.
let currentAccessToken = null;
supabase.auth.onAuthStateChange((_event, session) => {
  currentAccessToken = session?.access_token || null;
});

api.interceptors.request.use((config) => {
  if (currentAccessToken) config.headers.Authorization = `Bearer ${currentAccessToken}`;
  return config;
});

// Whole-app final-check finding: every catch block in this app does
// toast.error(e.response?.data?.detail || 'Error'), which works fine for
// every hand-written HTTPException(...) this backend raises (detail is a
// plain string) — but FastAPI's own automatic 422 (a Pydantic validation
// error, e.g. one of this session's new Field(le=..., allow_inf_nan=False)
// bounds getting violated by a mistyped amount) returns detail as an ARRAY
// of {loc, msg, type} objects instead. Passing that array straight into a
// toast isn't just an ugly message — sonner renders it as a React child
// with no type guard, and with no error boundary anywhere in this app,
// that throws and blanks the entire screen over what was, from the user's
// side, a normal data-entry mistake. Normalized here, once, so every
// existing and future e.response?.data?.detail call site downstream
// always gets a plain, readable string.
// Round 20 finding: server.py raises 401 in exactly one place (the shared
// auth dependency, "Not authenticated"/"Invalid or expired session" —
// confirmed by grep, nothing else in the backend uses it) but nothing
// here ever acted on that signal. An expired/invalid session mid-use
// (e.g. clicking Save after being idle) just fell through to whichever
// catch block was waiting, which showed a same-page toast while the app
// kept rendering as if still fully logged in -- no indication the user
// needed to sign in again, and every next action would silently fail
// the same way. `handled` stops a burst of concurrent requests that all
// 401 together (common right when a token dies) from stacking redirects/
// toasts; the pathname check avoids interfering with the login page's
// own (unauthenticated, never-401ing) calls. The hard redirect mirrors
// the existing Logout button's own window.location.href approach, which
// also resets every module-level cache (e.g. useMyCompanies' own
// company-list cache) -- closing a related narrow cross-session leak on
// a shared browser tab for free.
let sessionExpiredHandled = false;
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detail
        .map((d) => {
          if (d && typeof d === 'object') {
            const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : null;
            const msg = d.msg || JSON.stringify(d);
            return field ? `${field}: ${msg}` : msg;
          }
          return String(d);
        })
        .join('; ');
    } else if (detail && typeof detail === 'object') {
      error.response.data.detail = detail.msg || JSON.stringify(detail);
    }
    if (error?.response?.status === 401 && window.location.pathname !== '/login' && !sessionExpiredHandled) {
      sessionExpiredHandled = true;
      toast.error('Your session has expired — please sign in again.');
      // Round 21 finding: firing the hard navigation on the same tick as
      // the toast risked tearing down the page before sonner's own
      // render/paint cycle ever showed it -- a brief delay lets the user
      // actually read why they're being sent back to the login page,
      // instead of it just silently happening. sessionExpiredHandled is
      // already set above, so any other request that also 401s during
      // this window correctly no-ops instead of stacking more toasts.
      setTimeout(() => { window.location.href = '/login'; }, 1500);
    }
    return Promise.reject(error);
  }
);

export const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const num = Number(n);
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  return sign + '₹' + abs.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

export const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(d); }
};

// Round 21 finding: every hand-written jsPDF export in this app already
// (correctly) avoids fmtINR's ₹ symbol -- jsPDF's built-in fonts (the
// standard 14 PDF fonts) have no glyph for U+20B9, so it renders as a
// missing-glyph box, not a rupee sign. But instead of sharing fmtINR's
// actual number logic, each export hand-rolled its own bare
// `Rs ${Number(x).toLocaleString('en-IN')}`, which defaults to
// maximumFractionDigits: 3 (fmtINR caps at 2) and puts a negative sign
// after "Rs " instead of before it -- so the SAME number could print
// with different precision, and a negative balance with a different
// sign position, depending on whether it's on screen or in a PDF. This
// mirrors fmtINR's exact formatting (2dp cap, en-IN grouping,
// sign-before-prefix) with the ASCII-safe prefix PDFs need instead.
export const fmtINRPdf = (n) => {
  if (n === null || n === undefined || isNaN(n)) return 'Rs 0';
  const num = Number(n);
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  return sign + 'Rs ' + abs.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

// Round 21 finding: PDF exports' own "Generated: ..." timestamps used
// two different formats across the app (a bare toLocaleString() --
// locale-default, e.g. "31/8/2026, 8:03:25 am" -- vs. the more readable
// dateStyle/timeStyle already used by the generic report exporter, the
// on-screen print header, and the audit log). One shared helper for
// every export.
export const fmtGeneratedAt = () => new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

export const downloadCSV = (rows, filename, preambleLines) => {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return '';
    let s = String(v);
    // Whole-app final-check finding: RFC-4180 quote/comma/newline escaping
    // alone doesn't stop CSV formula injection -- a cell starting with
    // =/+/-/@ is evaluated as a live formula by Excel/Sheets on open
    // regardless of surrounding quoting (=HYPERLINK(...) can exfiltrate
    // adjacent cell data; =WEBSERVICE(...) can call out to a URL), and
    // this app has genuinely user-controlled text (expense/project
    // descriptions, entered by any accountant-tier login) flowing
    // straight into exported CSVs. A leading single quote is the
    // standard neutralizing prefix -- both apps show it as plain text,
    // not a live formula. The dedicated .xlsx export path is unaffected
    // (SheetJS writes these as typed string cells, no formula property).
    // Round 12 finding: a leading Tab or Carriage Return also needs the
    // same prefix -- Excel strips leading whitespace before evaluating
    // the first "real" character, so "\t=cmd(...)" is exploitable the
    // same way despite not starting with =/+/-/@ literally (OWASP's CSV
    // injection guidance lists tab/CR alongside those four for this
    // exact reason).
    if (/^[\t\r=+\-@]/.test(s)) s = "'" + s;
    s = s.replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const preamble = (preambleLines || []).map(l => escape(l)).map(l => l + '\n').join('');
  const csv = preamble + [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
